import React from 'react';
import { Box, styled } from '@mui/material';
import {
  numberToCurrencyString,
  numberToPercentageString,
  pluralize,
} from 'further-ui/utils';
import { CurrencyCode } from 'further-types/lp/vehicle';
import EqualisationsAllocationsTable from 'components/EqualisationElements/EqualisationsAllocationsTable';
import { AllocationRule } from 'further-types/lp/allocations';
import { Controller, useFormContext } from 'react-hook-form';
import CheckboxCell from 'components/EqualisationElements/CheckboxCell';
import useEqualiseRealisationAllocations from '../../hooks/useEqualiseRealisationAllocations';
import { Column as TableColumn } from 'further-ui/components/Table';
import NumberInput from 'further-ui/components/NumberInput';

type RealisationAllocation = {
  _id: string;
  holdingClassId: string;
  commitmentId: string;
  investmentId: string;
  investorLabel: string;
  allocationPercentage: number;
  originalAllocationPercentage: number;
  allocationType: AllocationRule;
  receivedConsiderationLcy: number;
  earnoutConsiderationLcy: number;
  escrowConsiderationLcy: number;
  withholdingTaxPayableLcy: number;
  withholdingTaxExempt: boolean;
  excludeFromAllocation?: boolean;
  unitsAllocated: number;
};

type EqualisationRealisationAllocationsFormProps = {
  fields?: Array<RealisationAllocation>;
  transactionCurrency?: CurrencyCode;
  allocationRule: AllocationRule;
  isNotEditable: boolean;
  actionIndex: number;
  allocationType:
    | 'originalRealisationAllocations'
    | 'newRealisationAllocations';
  linkedActionIndex?: number;
};

const StyledEmptyMessage = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  textAlign: 'center',
  color: theme.palette.text.secondary,
}));

const EqualisationRealisationAllocationsForm: React.FC<
  EqualisationRealisationAllocationsFormProps
> = ({
  transactionCurrency = CurrencyCode.GBP,
  isNotEditable,
  actionIndex,
  allocationType,
  linkedActionIndex,
  allocationRule,
}) => {
  const { control } = useFormContext();

  const shouldRender = linkedActionIndex !== undefined;

  const {
    allocationsPath,
    watchedFields,
    liveTotalAllocationPercentage,
    liveTotalUnitsAllocated,
    liveTotalReceivedConsiderationLcy,
    liveTotalEscrowConsiderationLcy,
    liveTotalEarnoutConsiderationLcy,
    liveTotalWithholdingTaxPayableLcy,
    handleAllocationPercentageChange,
    handleExcludeFromAllocationChange,
    handleExcludeFromWithholdingTaxChange,
    handleAllocationRuleChange,
  } = useEqualiseRealisationAllocations({
    actionIndex,
    linkedActionIndex: linkedActionIndex ?? 0,
    allocationType,
  });

  if (!shouldRender) {
    return (
      <StyledEmptyMessage>
        No realisation allocations available
      </StyledEmptyMessage>
    );
  }

  if (!watchedFields || watchedFields.length === 0) {
    return (
      <StyledEmptyMessage>
        No realisation allocations available
      </StyledEmptyMessage>
    );
  }

  const rows = watchedFields.map((field) => ({
    ...field,
    id: field.commitmentId,
  }));

  const columns: Array<TableColumn<(typeof rows)[number]>> = [
    {
      key: 'investor',
      label: 'Investor',
      render: (row) => row.investorLabel,
      footer: `Total from ${watchedFields?.length} ${pluralize(
        'investor',
        watchedFields?.length,
      )}`,
    },
    {
      key: 'allocationPercentage',
      label: 'Allocation (%)',
      render: (row, rowIndex) => (
        <Controller
          name={`${allocationsPath}.${rowIndex}.allocationPercentage`}
          control={control}
          render={({ field }) => (
            <NumberInput
              size="small"
              disabled={isNotEditable || row.excludeFromAllocation}
              value={field.value}
              onChange={(value) => {
                handleAllocationPercentageChange(value ?? 0, rowIndex);
              }}
            />
          )}
        />
      ),
      footer: numberToPercentageString(liveTotalAllocationPercentage, {
        accuracy: 2,
        forceDecimals: true,
      }),
    },
    {
      key: 'unitsAllocated',
      label: 'Units allocated',
      render: (row) => row.unitsAllocated.toLocaleString(),
      footer: liveTotalUnitsAllocated.toLocaleString(),
    },
    {
      key: 'receivedConsiderationLcy',
      label: 'Received consideration',
      render: (row) =>
        numberToCurrencyString(row.receivedConsiderationLcy, {
          currency: transactionCurrency,
        }),
      footer: numberToCurrencyString(liveTotalReceivedConsiderationLcy, {
        currency: transactionCurrency,
      }),
    },
    {
      key: 'escrowConsiderationLcy',
      label: 'Escrow consideration',
      render: (row) =>
        numberToCurrencyString(row.escrowConsiderationLcy, {
          currency: transactionCurrency,
        }),
      footer: numberToCurrencyString(liveTotalEscrowConsiderationLcy, {
        currency: transactionCurrency,
      }),
    },
    {
      key: 'earnoutConsiderationLcy',
      label: 'Earnout consideration',
      render: (row) =>
        numberToCurrencyString(row.earnoutConsiderationLcy, {
          currency: transactionCurrency,
        }),
      footer: numberToCurrencyString(liveTotalEarnoutConsiderationLcy, {
        currency: transactionCurrency,
      }),
    },
    {
      key: 'withholdingTaxPayableLcy',
      label: 'Withholding tax',
      render: (row) =>
        numberToCurrencyString(row.withholdingTaxPayableLcy, {
          currency: transactionCurrency,
        }),
      footer: numberToCurrencyString(liveTotalWithholdingTaxPayableLcy, {
        currency: transactionCurrency,
      }),
    },
    {
      key: 'withholdingTaxExempt',
      label: 'Exclude user from withholding tax',
      render: (_row, rowIndex) => (
        <CheckboxCell
          control={control}
          name={`${allocationsPath}.${rowIndex}.withholdingTaxExempt`}
          onChange={(value) =>
            handleExcludeFromWithholdingTaxChange(value, rowIndex)
          }
          disabled={isNotEditable || _row.excludeFromAllocation}
        />
      ),
    },
  ];

  return (
    <EqualisationsAllocationsTable
      columns={columns}
      rows={rows}
      showAllocationRule={true}
      isNotEditable={isNotEditable}
      onChangeAllocationRule={(value) => {
        handleAllocationRuleChange(value);
      }}
      allocationRule={allocationRule}
      allocationRuleOptions={[
        {
          value: AllocationRule.PerOriginalInvestment,
          label: 'Per original investment',
        },
        {
          value: AllocationRule.Manual,
          label: 'Manual allocation',
        },
      ]}
      checkboxes={{
        isCheckedRow: (row) => !row.excludeFromAllocation,
        isDisabledRow: () => isNotEditable,
        onRowCheckedToggle: (row, rowIndex) => {
          handleExcludeFromAllocationChange(
            !row.excludeFromAllocation,
            rowIndex,
          );
        },
      }}
    />
  );
};

export default EqualisationRealisationAllocationsForm;
